import { useEffect, useRef } from 'react';

import { formatHours, type ApprovalInboxEntry } from '@time-tracking/shared';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { daysBetweenIso } from '../../utils/weekDates.js';

interface TeamGroup {
  teamId: string;
  teamName: string;
  leadName: string | null;
  stuckEntries: ApprovalInboxEntry[];
}

interface AoboConfirmDialogProps {
  team: TeamGroup | null; // null = closed
  /**
   * The America/Chicago calendar date, supplied by the caller rather than read from the
   * clock here.
   *
   * This component used to derive it from `new Date().toISOString()` — the UTC day —
   * which between 19:00 and midnight Central is already tomorrow, so each row's
   * "submitted N days ago" read a day high. It agreed with the caller's own stuck
   * threshold only because that had the identical bug: the list and the label were
   * wrong together, which is why neither looked wrong.
   */
  today: string;
  onClose: () => void;
  onConfirm: (timesheetIds: string[]) => void;
  isPending: boolean;
}

function daysSince(isoDate: string | null, today: string): number {
  return isoDate === null ? 0 : daysBetweenIso(isoDate, today);
}

export function AoboConfirmDialog({
  team,
  today,
  onClose,
  onConfirm,
  isPending,
}: AoboConfirmDialogProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useFocusTrap(dialogRef, Boolean(team));

  useEffect(() => {
    if (team) {
      confirmBtnRef.current?.focus();
    }
  }, [team]);

  useEffect(() => {
    if (!team) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [team, onClose]);

  if (!team) return null;

  const stuckIds = team.stuckEntries
    .map((e) => e.timesheetId)
    .filter((id): id is string => id !== null);

  const handleConfirm = () => {
    onConfirm(stuckIds);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="aobo-dialog-title"
      className="dialog-overlay"
    >
      <div ref={dialogRef} className="dialog dialog--sm aobo-dialog">
        <h2 id="aobo-dialog-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" />
          </svg>
          Approve on behalf of {team.leadName ?? team.teamName}
        </h2>

        <ul className="aobo-dialog__stuck-list">
          {team.stuckEntries.map((entry) => (
            <li key={entry.userId} className="aobo-dialog__stuck-item">
              <span>{entry.name}</span>
              <span>
                {formatHours(entry.totalMinutes)}h &middot; submitted{' '}
                {daysSince(entry.submittedAt, today)}{' '}
                {daysSince(entry.submittedAt, today) === 1 ? 'day' : 'days'} ago
              </span>
            </li>
          ))}
        </ul>

        <p className="aobo-dialog__audit-note">
          You are acting as approver for this team. Your name will appear in the audit log.
        </p>

        <div className="aobo-dialog__actions">
          <button
            ref={confirmBtnRef}
            type="button"
            className="btn primary"
            disabled={isPending}
            onClick={handleConfirm}
          >
            Approve {stuckIds.length} on behalf of {team.leadName ?? team.teamName}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
